import { useState, useRef, useEffect } from "react";
import useAutocomplete from "@mui/material/useAutocomplete";
import { styled } from "@mui/system";

const Label = styled("label")({
  display: "block",
});

const Input = styled("input")(({ theme }) => ({
  width: "100%",
  backgroundColor: "#fff",
  color: "#000",
  ...theme.applyStyles("dark", {
    backgroundColor: "#000",
    color: "#fff",
  }),
}));

export default function CustomAutocomplete({
  options,
  optionLabel,
  optionDisplay,
  maxDisplayOptions = -1,
  width = 200,
  onSelect,
}) {
  const [currentInputValue, setCurrentInputValue] = useState("");
  const [prevValue, setPrevValue] = useState("");
  const changedValue = useRef(false);

  const Listbox = styled("ul")(({ theme }) => ({
    width: width,
    margin: 0,
    padding: 0,
    zIndex: 1,
    position: "absolute",
    listStyle: "none",
    backgroundColor: "#fff",
    overflow: "auto",
    maxHeight: 200,
    border: "1px solid rgba(0,0,0,.25)",
    "& li:hover": {
      backgroundColor: "#4a8df6",
      color: "white",
      cursor: "pointer",
    },
    "& li:active": {
      backgroundColor: "#2977f5",
      color: "white",
    },
    ...theme.applyStyles("dark", {
      backgroundColor: "#000",
    }),
    boxSizing: "border-box",
  }));

  const selectOption = (newValue, onSelect) => {
    changedValue.current = true;
    if (!newValue) return;
    onSelect(newValue);
    setPrevValue(newValue);
    setCurrentInputValue("");
  };

  const trySelectOption = () => {
    if (!changedValue.current) {
      selectOption(prevValue, onSelect);
    }
    changedValue.current = false;
  };

  const {
    getRootProps,
    getInputLabelProps,
    getInputProps,
    getListboxProps,
    getOptionProps,
    groupedOptions,
  } = useAutocomplete({
    id: "use-autocomplete-demo",
    options: options,
    isbra: false,
    disablePortal: true,
    disableListWrap: true,
    onInputChange: (e, value) => setCurrentInputValue(value),
    getOptionLabel: (option) => optionLabel(option),
    onChange: (event, newValue) => selectOption(newValue, onSelect),
    onClose: (event, reason) => {
      if (reason === "selectOption") {
        trySelectOption();
      }
    },
  });

  return (
    <div>
      <div {...getRootProps()}>
        <Label {...getInputLabelProps()}></Label>
        <Input
          placeholder="Cerca..."
          {...getInputProps()}
          value={currentInputValue}
        />
      </div>
      {groupedOptions.length > 0 ? (
        <Listbox {...getListboxProps()}>
          {groupedOptions.map((option, index) => {
            if (
              index <
              (maxDisplayOptions < 0
                ? groupedOptions.length
                : maxDisplayOptions)
            ) {
              const { key, ...optionProps } = getOptionProps({ option, index });
              return (
                <li key={index} {...optionProps}>
                  {optionDisplay(option)}
                </li>
              );
            }
          })}
        </Listbox>
      ) : null}
    </div>
  );
}
